import { IControlOption } from '@/Interfaces/ControlOption.interface';
import FormErrorHelperText from '@/components/Form/FormErrorHelperText';
import SearchIcon from '@/components/Icons/SearchIcon';
import SelectAllOption from '@/components/TabSelect/SelectAllOption';
import StyledInput from '@/components/TabSelect/styled/StyledInput';
import { GREY } from '@/constants/color';
import { Box, Checkbox, FormControlLabel, FormGroup, SxProps, Theme } from '@mui/material';
import debounce from 'debounce';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

interface IProps<T> {
  id: string;
  value: T[];
  options: T[];
  onChange: (v: T[]) => void;
  label: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  listboxSx?: SxProps<Theme>;
  error?: string;
}

const SearchList = <T extends IControlOption<any, any>>({
  id,
  options = [],
  value = [],
  disabled,
  onChange,
  sx,
  listboxSx,
  label,
  error,
}: IProps<T>) => {
  const [search, setSearch] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const displayedOptions = useMemo(() => {
    if (!searchTerm.length) {
      return options;
    }
    return options.filter(option => option.label.toLowerCase().includes(searchTerm));
  }, [searchTerm, options]);

  //#region useMemo
  const allSelected = useMemo(
    () => (searchTerm.length ? value?.length === displayedOptions.length : value?.length === options.length),
    [value, options, displayedOptions, searchTerm],
  );

  const partialSelection = useMemo(() => searchTerm.length > 0, [searchTerm]);
  //#endregion

  //#region useCallback
  const valueSet = useMemo<Set<T['value']>>(() => new Set(value?.map(v => v.value) ?? []), [value]);

  const isOptionChecked = useCallback((id: T['value']) => !!valueSet?.has(id), [valueSet]);

  const handleChange = useCallback(
    (selectedValue: T) => {
      if (isOptionChecked(selectedValue.value)) {
        const newValue = value.filter((v: T) => v.value !== selectedValue.value);
        onChange(newValue);
        return;
      }
      onChange([...(value || []), selectedValue]);
    },
    [onChange, value, isOptionChecked],
  );

  const handleClearOptions = useCallback(() => onChange([]), [onChange]);

  const updateSearchTerm = useCallback(
    debounce((newSearch: string) => {
      setSearchTerm(newSearch.trim().toLowerCase());
    }, 200),
    [],
  );

  const onSearchValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newSearch = e.target.value || '';
      setSearch(newSearch);
      updateSearchTerm(newSearch);
    },
    [updateSearchTerm],
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        onChange(displayedOptions);
      } else {
        handleClearOptions();
      }
    },
    [displayedOptions, handleClearOptions, onChange],
  );

  const handleToggleSelectAll = useCallback(() => {
    handleSelectAll?.(!allSelected);
  }, [allSelected, handleSelectAll]);
  //#endregion

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
      <div className='flex gap-2 mb-1'>
        <StyledInput
          id={id}
          fullWidth
          startAdornment={<SearchIcon className={`text-[${GREY}]`} />}
          placeholder='Type to search'
          value={search}
          onChange={onSearchValueChange}
          sx={{ padding: '0 7px 0 16px' }}
          disabled={disabled}
        />
      </div>
      {error ? <FormErrorHelperText error>{error}</FormErrorHelperText> : null}
      {displayedOptions.length ? (
        <>
          <SelectAllOption
            total={displayedOptions.length}
            indeterminate={!allSelected && !!value?.length}
            checked={allSelected}
            onChange={handleToggleSelectAll}
            label={label}
            partialSelection={partialSelection}
            sx={{ flexGrow: 0 }}
            disabled={disabled}
          />
          <hr />
        </>
      ) : (
        <div className='m-auto'>No options</div>
      )}
      <FormGroup sx={{ overflowY: 'auto', flexWrap: 'nowrap', ...listboxSx }}>
        {displayedOptions.map(option => (
          <FormControlLabel
            key={option.value}
            sx={{
              ':hover': { background: '#1b43321f' },
              fontSize: '16px',
              marginLeft: '12px',
              borderRadius: '6px',
            }}
            control={
              <Checkbox
                sx={{ padding: '9px' }}
                value={option.value}
                disabled={disabled}
                checked={isOptionChecked(option.value)}
                onChange={() => handleChange(option)}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

export default SearchList;
